import { PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import {
  useCreateEnrollmentCode,
  useMyConnectorDevices,
  useRevokeConnectorDevice,
} from '@/products/helpdesk/features/remote-support/use-remote-support';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

/** Settings card: pair the Windows connector, list and revoke own devices. */
export function ConnectorPairingCard() {
  const me = useMe();
  const allowed = hasPermission(me.data, PermissionCode.REMOTE_SUPPORT_REQUEST);
  const devices = useMyConnectorDevices(allowed);
  const createCode = useCreateEnrollmentCode();
  const revoke = useRevokeConnectorDevice();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);

  async function onCreateCode() {
    try {
      const result = await createCode.mutateAsync();
      setCode(result);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Kopplungscode konnte nicht erzeugt werden.',
      );
    }
  }

  async function onRevoke(id: string) {
    try {
      await revoke.mutateAsync(id);
      toast.success('Gerät widerrufen. Der Connector muss neu gekoppelt werden.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Gerät konnte nicht widerrufen werden.',
      );
    }
  }

  const active = devices.data?.items.filter((item) => item.isActive) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fernwartung (Mitarbeiterportal Connector)</CardTitle>
        <CardDescription>
          Der Mitarbeiterportal Connector ist ein kleines Windows-Programm (nicht diese Website und
          nicht RustDesk). Es sitzt in der Taskleiste, holt Ihre Zustimmung ein und spricht mit dem
          Mitarbeiterportal. RustDesk ist das separate Bildschirm-Programm.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!allowed && (
          <p className="text-sm text-muted-foreground">
            Fernwartung ist für Ihr Konto noch nicht aktiv. Bitte laden Sie die Seite neu (einmal
            ab- und anmelden reicht, falls der Server gerade aktualisiert wurde).
          </p>
        )}

        {allowed && (
          <>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Was auf dem PC installiert sein muss</p>
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  <span className="font-medium text-foreground">RustDesk</span> unter{' '}
                  <code>C:\Program Files\RustDesk\</code> (von der IT, kein Download aus dem
                  Internet).
                </li>
                <li>
                  <span className="font-medium text-foreground">Mitarbeiterportal Connector</span> (
                  <code>GbTicketConnector.exe</code>). Die IT verteilt ihn per
                  Intune/Softwarecenter; es gibt hier keinen Download. Danach: Startmenü
                  „Mitarbeiterportal Connector“ oder Symbol unten rechts in der Taskleiste (ggf.
                  Pfeil „Ausgeblendete Symbole“).
                </li>
              </ol>
              <p>
                Fehlt eines von beiden: IT-Helpdesk / Softwarecenter, nicht selbst aus dem Netz
                laden. Der Kopplungscode kommt erst <span className="font-medium">nach</span> der
                Installation — und nur in den Connector, nie in RustDesk.
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-medium">Gekoppelte Geräte</p>
              {devices.isPending && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" aria-hidden /> Wird geladen …
                </p>
              )}
              {devices.data && active.length === 0 && (
                <p className="text-sm text-muted-foreground">Noch kein Gerät gekoppelt.</p>
              )}
              <ul className="space-y-2">
                {active.map((device) => (
                  <li
                    key={device.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
                  >
                    <div>
                      <p className="font-medium">{device.hostname}</p>
                      <p className="text-xs text-muted-foreground">
                        Geräte-GUID {device.deviceId} · gekoppelt{' '}
                        {formatDateTime(device.registeredAt)}
                        {device.lastSeenAt
                          ? ` · zuletzt online ${formatDateTime(device.lastSeenAt)}`
                          : ' · noch nie online'}
                        {device.connectorVersion ? ` · v${device.connectorVersion}` : ''}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void onRevoke(device.id)}
                      disabled={revoke.isPending}
                    >
                      Widerrufen
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <p className="font-medium">Neues Gerät koppeln</p>
              <p className="text-sm text-muted-foreground">
                Den Code nur im Mitarbeiterportal Connector eingeben, niemals in RustDesk. Gültig 10
                Minuten, einmal verwendbar.
              </p>
              {code ? (
                <div className="space-y-3 rounded-md border bg-muted/40 p-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Kopplungscode</p>
                    <p className="font-mono text-2xl tracking-widest">{code.code}</p>
                    <p className="text-xs text-muted-foreground">
                      Gültig bis {formatDateTime(code.expiresAt)}.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">Jetzt auf diesem PC</p>
                    <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                      <li>
                        Mitarbeiterportal Connector starten. Unten rechts in der Taskleiste
                        erscheint das Symbol.
                      </li>
                      <li>Rechtsklick auf das Symbol → „Mit Mitarbeiterportal koppeln“.</li>
                      <li>
                        API-Adresse (<code>https://…/api</code>) und den Kopplungscode oben
                        eingeben.
                      </li>
                      <li>
                        RustDesk muss unter Program Files installiert sein. Es startet erst später
                        am Ticket nach „Zulassen“, nicht durch diesen Code.
                      </li>
                    </ol>
                  </div>
                </div>
              ) : (
                <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                  <li>Unten „Kopplungscode erzeugen“ klicken.</li>
                  <li>
                    Mitarbeiterportal Connector auf dem Rechner starten (Symbol in der Taskleiste).
                  </li>
                  <li>
                    Rechtsklick auf das Symbol → „Mit Mitarbeiterportal koppeln“. API-Adresse (
                    <code>https://…/api</code>) und den Code eingeben.
                  </li>
                  <li>
                    RustDesk muss unter Program Files installiert sein. Gestartet wird es erst am
                    Ticket nach „Zulassen“, nicht durch diesen Code.
                  </li>
                </ol>
              )}
              <Button size="sm" onClick={() => void onCreateCode()} disabled={createCode.isPending}>
                {createCode.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                {code ? 'Neuen Code erzeugen' : 'Kopplungscode erzeugen'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
